import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import Select from 'react-select';
import Tippy from '@tippyjs/react';
import FunctionSelect from './FunctionSelect';
import darkSelectStyles from '../../../shared/darkSelectStyles';

const canSubmitForm = (formData, roles, isSubmitting) => {
  if (isSubmitting) {
    return false;
  }

  const role = roles.find((r) => r.id === formData.roleId);

  if (!role) {
    return false;
  }

  if (role.name === 'Promoter') {
    return (
      formData.firstName &&
      formData.lastName &&
      formData.email &&
      formData.roleId &&
      formData.company
    );
  } else {
    return (
      formData.firstName &&
      formData.lastName &&
      formData.email &&
      formData.roleId
    );
  }
};

const TeamMemberModal = ({
  isOpen,
  onClose,
  mode,
  formData,
  roles,
  venueOwnerships,
  team,
  csrfToken,
  enableAllVenues,
  isSubmitting,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onRoleIdChange,
  onCompanyChange,
  onVenueOwnershipClick,
  onEnableAllVenuesClick,
  onDisableAllVenuesClick,
  onSubmit,
  teamFunctions,
  onTeamFunctionIdsChange,
  onCreateTeamFunction
}) => {
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen || !formData) {
    return null;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSubmitForm(formData, roles, isSubmitting)) {
      onSubmit(csrfToken, venueOwnerships, formData, team);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const selectedRole = roles.find((r) => r.id === formData.roleId);
  const isPromoter = selectedRole?.name === 'Promoter';
  const isEditing = mode === 'edit';

  const availableRoles = roles.filter((role) => {
    if (role.name === 'Guest') {
      const guestRole = roles.find(r => r.name === 'Guest');
      return formData.roleId === guestRole?.id;
    }
    return true;
  });

  return (
    <div className="team-modal-overlay" onClick={handleOverlayClick}>
      <div className="team-modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="team-modal-header">
          <h3 id="modal-title" className="team-modal-title">
            {isEditing ? 'Edit team member' : 'Add team member'}
          </h3>
          <button className="team-modal-close" onClick={onClose} aria-label="Close">
            <i className="fa fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="team-modal-body">
            <div className="team-form-row">
              <div className="team-form-group">
                <label className="team-form-label">First name</label>
                <input
                  type="text"
                  className="team-form-input"
                  value={formData.firstName || ''}
                  onChange={(e) => onFirstNameChange(e.target.value)}
                  placeholder="Enter first name"
                />
              </div>
              <div className="team-form-group">
                <label className="team-form-label">Last name</label>
                <input
                  type="text"
                  className="team-form-input"
                  value={formData.lastName || ''}
                  onChange={(e) => onLastNameChange(e.target.value)}
                  placeholder="Enter last name"
                />
              </div>
            </div>

            <div className="team-form-group">
              <label className="team-form-label">Email address</label>
              <input
                type="email"
                className="team-form-input"
                value={formData.email || ''}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="Enter email address"
              />
            </div>

            <div className="team-form-group">
              <label className="team-form-label">
                Role
                <Tippy
                  content={
                    <div>
                      {roles.map((role) => (
                        <div key={role.id} style={{ marginBottom: '6px' }}>
                          <strong>{role.display_name}</strong>: {role.description}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <i className="far fa-info-circle text-muted ml-2" style={{ cursor: 'pointer' }}></i>
                </Tippy>
              </label>
              <Select
                options={availableRoles.map((role) => ({ value: role.id, label: role.display_name }))}
                value={formData.roleId ? { value: formData.roleId, label: roles.find(r => r.id === formData.roleId)?.display_name } : null}
                onChange={(option) => onRoleIdChange(option ? option.value : null)}
                placeholder="Select a role"
                isClearable={false}
                styles={darkSelectStyles}
              />
              {selectedRole && (
                <p style={{ marginTop: '4px', fontSize: '13px', color: 'var(--sl-text-placeholder, #717680)' }}>
                  {selectedRole.description}
                </p>
              )}
            </div>

            <div className="team-form-group">
              <label className="team-form-label">Function</label>
              <FunctionSelect
                teamFunctions={teamFunctions}
                values={formData.teamFunctionIds || []}
                onChange={onTeamFunctionIdsChange}
                onCreate={async (name) => {
                  const created = await onCreateTeamFunction(csrfToken, team, name);
                  return created;
                }}
              />
            </div>

            {isPromoter && (
              <div className="team-form-group">
                <label className="team-form-label">Company</label>
                <input
                  type="text"
                  className="team-form-input"
                  value={formData.company || ''}
                  onChange={(e) => onCompanyChange(e.target.value)}
                  placeholder="Enter company name"
                />
              </div>
            )}

            <div className="team-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <label className="team-form-label" style={{ marginBottom: 0 }}>Venue Access</label>
                {enableAllVenues ? (
                  <button
                    type="button"
                    className="team-btn-tertiary"
                    onClick={() => onEnableAllVenuesClick(venueOwnerships)}
                  >
                    Enable All
                  </button>
                ) : (
                  <button
                    type="button"
                    className="team-btn-tertiary"
                    onClick={() => onDisableAllVenuesClick()}
                  >
                    Disable All
                  </button>
                )}
              </div>
              <div className="team-venue-checkboxes">
                {[...venueOwnerships].sort((a, b) => a.venueable.name.localeCompare(b.venueable.name)).map((venueOwnership) => (
                  <div key={venueOwnership.id} className="team-venue-checkbox-item">
                    <input
                      type="checkbox"
                      id={`modal-venue-${venueOwnership.id}`}
                      className="team-checkbox"
                      style={{ marginTop: '1px' }}
                      checked={
                        formData.venueOwnershipIds?.includes(venueOwnership.id) || false
                      }
                      onChange={() => onVenueOwnershipClick(venueOwnership.id)}
                    />
                    <label
                      htmlFor={`modal-venue-${venueOwnership.id}`}
                      className="team-venue-checkbox-label"
                    >
                      {venueOwnership.venueable.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {Array.isArray(formData.errors) && formData.errors.length > 0 && (
              <div className="team-form-error-box">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {formData.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="team-modal-footer">
            <button type="button" className="team-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="team-btn-primary"
              disabled={!canSubmitForm(formData, roles, isSubmitting)}
            >
              {isSubmitting ? (
                <>
                  <img src="/uploading-loading.gif" className="mr-1" style={{ width: '16px' }} alt="" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                isEditing ? 'Save Changes' : 'Add Team Member'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

TeamMemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  formData: PropTypes.object,
  roles: PropTypes.array.isRequired,
  venueOwnerships: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  enableAllVenues: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onFirstNameChange: PropTypes.func.isRequired,
  onLastNameChange: PropTypes.func.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onRoleIdChange: PropTypes.func.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  onVenueOwnershipClick: PropTypes.func.isRequired,
  onEnableAllVenuesClick: PropTypes.func.isRequired,
  onDisableAllVenuesClick: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  teamFunctions: PropTypes.array.isRequired,
  onTeamFunctionIdsChange: PropTypes.func.isRequired,
  onCreateTeamFunction: PropTypes.func.isRequired
};

export default TeamMemberModal;
